/**
 * Owner/root lifecycle utilities for reactive scopes.
 *
 * This keeps cleanup structural instead of emitter-local/manual and follows the
 * smallest transferable part of Solid/Svelte's runtime ownership models.
 *
 * @module @smoothie-framework/ui/state
 */
import { defaultFlushContext } from './flush-context';
let currentOwner = null;
export function createRoot(fn, options = {}) {
    const owner = createOwner(currentOwner, undefined, options.flushContext);
    try {
        return runWithOwner(owner, () => fn(() => disposeOwner(owner)));
    }
    catch (error) {
        disposeOwner(owner);
        throw error;
    }
}
export function onCleanup(fn) {
    if (!currentOwner || currentOwner.disposed) {
        return;
    }
    (currentOwner.cleanups ?? (currentOwner.cleanups = [])).push(fn);
}
export function getOwner() {
    return currentOwner;
}
export function runWithOwner(owner, fn) {
    const previousOwner = currentOwner;
    currentOwner = owner;
    try {
        return fn();
    }
    finally {
        currentOwner = previousOwner;
    }
}
export function createOwner(parent = currentOwner, disposeHook, flushContext = parent?.flushContext ?? defaultFlushContext) {
    const owner = {
        parent,
        children: null,
        cleanups: null,
        disposeHook,
        flushContext,
        disposed: false,
    };
    if (parent) {
        (parent.children ?? (parent.children = new Set())).add(owner);
    }
    return owner;
}
export function resetOwner(owner) {
    const children = owner.children;
    owner.children = null;
    if (children) {
        for (const child of children) {
            disposeOwner(child);
        }
    }
    const cleanups = owner.cleanups;
    owner.cleanups = null;
    if (cleanups) {
        for (let index = cleanups.length - 1; index >= 0; index -= 1) {
            cleanups[index]?.();
        }
    }
}
export function disposeOwner(owner) {
    if (owner.disposed) {
        return;
    }
    owner.disposed = true;
    owner.parent?.children?.delete(owner);
    owner.disposeHook?.();
    resetOwner(owner);
}
//# sourceMappingURL=scope.js.map